import React from 'react';
import BayerDither from './BayerDither';
import './ui.css';

/** Shared page hierarchy. Callers keep ownership of their copy and content. */
export function PageIntro({ title, description, children, className = '' }: {
    title: React.ReactNode;
    description?: React.ReactNode;
    children?: React.ReactNode;
    className?: string;
}) {
    return (
        <header className={`bs-page-intro ${className}`}>
            <BayerDither className="bs-page-intro__dither" />
            <div className="bs-page-intro__content">
                <h1 className="bs-display">{title}</h1>
                {description && <p className="bs-lede bs-page-intro__description">{description}</p>}
                {children && <div className="bs-page-intro__details">{children}</div>}
            </div>
        </header>
    );
}

/** Single-choice controls use pressed buttons, without claiming tab panels. */
export function ChoiceGroup<T extends string>({ label, options, value, onChange, className = '', variant = 'underline' }: {
    label: string;
    options: readonly { value: T; label: React.ReactNode }[];
    value: T;
    onChange: (value: T) => void;
    className?: string;
    variant?: 'underline' | 'filled';
}) {
    return (
        <div className={`bs-choices${variant === 'filled' ? ' bs-choices--filled' : ''} ${className}`} role="group" aria-label={label}>
            {options.map(option => (
                <button type="button" key={option.value} className="tap-hit" aria-pressed={value === option.value}
                    onClick={() => onChange(option.value)}>
                    {option.label}
                </button>
            ))}
        </div>
    );
}

/** A useful empty/error state keeps its explanation and next action together. */
export function Feedback({ title, children, tone = 'default', action }: {
    title?: string;
    children: React.ReactNode;
    tone?: 'default' | 'error';
    action?: React.ReactNode;
}) {
    return (
        <section className={`bs-feedback bs-feedback--${tone}`} role={tone === 'error' ? 'alert' : 'status'}>
            {title && <h2 className="bs-h2">{title}</h2>}
            <div className="bs-feedback__body">{children}</div>
            {action && <div className="bs-feedback__action">{action}</div>}
        </section>
    );
}
